import { makeStyles } from '@mui/styles';
import React from 'react';
import { useSelector } from 'react-redux';

import { DownloadCell } from './DownloadCell';
import SavedSearchesTable from '../../../../components/SavedSearchesTable';
import withTranslation from '../../../../components/withTranslation';
import {
  OFFLINE_ENTITY_TYPE,
  OFFLINE_TAB_PART_ID,
} from '../../../../form-designer/constants/runtime.constants';
import { getIsOfflineSupportAvailable } from '../../../../form-designer/selectors/offlineMode.selectors';
import { getDashboardConfig } from '../../../../selectors/DashboardSelectors';
import { includesValueInOptions } from '../../../../utils/commonUtils';
import NavigateLinkCell from '../../../FindPage/NavigateLinkCell';
import CustomHeaderActions from '../CustomHeaderActions';
import DashboardTabSelector from '../DashboardTabSelector';

const useStyles = makeStyles({
  root: {
    '& [class*="MuiPaper"]': {
      overflowX: 'auto',
      overflowY: 'hidden',
    },
    '& [class*="MuiTable-root"]': {
      minHeight: 486.856,
    },
  },
  tabSelector: {
    marginLeft: -16,
    marginTop: -16,
  },
});

const shouldAddDownloadCellForRow = (row) => {
  const rowEntityType = row.EntityType;
  const rowWftName = row.WFT_NAME;
  const rowWftStepId = row.WFT_STEP_ID;
  const hasReviewOrPerformStepId = includesValueInOptions(rowWftStepId, [
    'performaudit',
    'reviewfinding',
  ]);
  const hasReviewOrPerformName = includesValueInOptions(rowWftName, [
    'Perform Audit',
    'Review Finding',
  ]);

  return (
    OFFLINE_ENTITY_TYPE === rowEntityType && (hasReviewOrPerformStepId || hasReviewOrPerformName)
  );
};

const DashboardTabTable = ({ mode, part, tab, onTabChange, onEdit, onDelete, translateString }) => {
  const classes = useStyles();

  const { isEditGlobal } = useSelector(getDashboardConfig);
  const isOfflineSupportAvailable = useSelector(getIsOfflineSupportAvailable);

  const { tabs } = part;
  const { id, title, subtitle, graph, editable, deletable } = tab;
  const { entityName, savedSearchName } = graph.savedSearchByEntity;

  const isEditMode = mode === 'edit';
  const isDeletable = isEditMode && (deletable || (title === 'My Training Tasks' && isEditGlobal));
  // FIXME: Temporary solution, it's needed to add a flag to a dashboard part/tab config to enable download buttons
  const isOfflineDownloadEnabled = id === OFFLINE_TAB_PART_ID && isOfflineSupportAvailable;

  function tabSelector() {
    const tabIndex = tabs.findIndex((tab) => tab.id === id);
    return (
      <div className={classes.tabSelector}>
        <DashboardTabSelector tabs={tabs} value={tabIndex} onChange={onTabChange} />
      </div>
    );
  }

  const tabTitle = (
    <>
      {tabs.length > 1 && tabSelector()}
      {tabs.length < 2 && translateString(title)}
    </>
  );

  const extraColumns = [
    {
      header: '',
      accessor: 'OFFLINE_MODE',
      dataType: 'System.String',
      // eslint-disable-next-line react/display-name
      customCell: ({ row }) => {
        if (shouldAddDownloadCellForRow(row)) {
          return <DownloadCell row={row} />;
        }

        return null;
      },
    },
  ];

  return (
    <div className={classes.root} ui-ta={translateString(title)}>
      <SavedSearchesTable
        title={tabTitle}
        subtitle={translateString(subtitle)}
        entityName={entityName}
        dataKey={id}
        initialSearchName={savedSearchName}
        rowsPerPage={6}
        rowsPerPageOptions={[6, 12, 24, 48]}
        showSearchSelector={false}
        customCell={NavigateLinkCell.fromNavigationColumn}
        active={isEditMode}
        hideAdvancedSearch={isEditMode}
        hideExport={isEditMode}
        CustomHeaderActions={() => (
          <CustomHeaderActions
            tabs={tabs}
            showEdit={editable && isEditMode}
            showDelete={isDeletable}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        )}
        enableSorting
        tablePaginationProps={{
          labelRowsPerPage: translateString('Rows'),
          labelDisplayedRows: ({ from, to, count }) =>
            `${from}-${to} ${translateString('of')} ${count}`,
          pageText: translateString('Page'),
          ofText: translateString('of'),
        }}
        enableSaving={true}
        extraColumns={isOfflineDownloadEnabled ? extraColumns : []}
        showFilters
      />
    </div>
  );
};

export default withTranslation(DashboardTabTable);
